import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/api';
import { fetchWithAuth } from '../../services/api';

interface User {
  userId: string;
  username: string;
  fullName: string;
  email: string;
  department: string;
  position: string;
  roles: string;
  active: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

interface Role {
  roleId: number;
  roleCode: string;
  roleName: string;
  description?: string;
  isActive: boolean;
}

interface UserManagementPageProps {
  onNavigate: (page: string, data?: any) => void;
}

export function UserManagementPage({ onNavigate }: UserManagementPageProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

  // Search and filter states
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState('username_asc');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [keyword, statusFilter, roleFilter, sortBy]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(getApiUrl('/users'));

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          let filtered = data.data;

          // ステータスフィルター
          if (statusFilter !== undefined) {
            filtered = filtered.filter((user: User) => user.active === statusFilter);
          }

          // ロールフィルター
          if (roleFilter) {
            filtered = filtered.filter((user: User) => {
              const userRoles = user.roles ? user.roles.split(',').map(r => r.trim()) : [];
              return userRoles.includes(roleFilter);
            });
          }

          // キーワード検索
          if (keyword) {
            const term = keyword.toLowerCase();
            filtered = filtered.filter((user: User) =>
              user.fullName.toLowerCase().includes(term) ||
              user.username.toLowerCase().includes(term) ||
              user.email.toLowerCase().includes(term) ||
              user.department.toLowerCase().includes(term)
            );
          }

          // フロントエンドでソート
          const sorted = [...filtered].sort((a: User, b: User) => {
            const [field, direction] = sortBy.split('_');
            let aValue: any = a[field as keyof User];
            let bValue: any = b[field as keyof User];

            // 日付の場合は比較用に変換
            if (field === 'lastLoginAt') {
              aValue = aValue ? new Date(aValue).getTime() : 0;
              bValue = bValue ? new Date(bValue).getTime() : 0;
            }

            if (direction === 'asc') {
              return aValue > bValue ? 1 : -1;
            } else {
              return aValue < bValue ? 1 : -1;
            }
          });

          setUsers(sorted);
        } else {
          setError(data.message || 'ユーザーの取得に失敗しました');
        }
      } else {
        setError('ユーザーの取得に失敗しました');
      }
    } catch (error) {
      console.error('ユーザー取得エラー:', error);
      setError('ユーザーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetchWithAuth(getApiUrl('/admin/roles'));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableRoles(data.data);
        }
      }
    } catch (error) {
      console.error('ロール取得エラー:', error);
    }
  };

  const handleCreate = () => {
    onNavigate('user-detail');
  };

  const handleEdit = (userId: string) => {
    onNavigate('user-detail', { userId });
  };

  const formatRoles = (roles: string) => {
    if (!roles) return '';
    const roleArray = roles.split(',').map(r => r.trim());
    const roleLabels = roleArray.map(role => {
      const option = availableRoles.find(r => r.roleCode === role);
      return option ? option.roleName : role;
    });
    return roleLabels.join(', ');
  };

  const formatLastLogin = (dateString?: string) => {
    if (!dateString) return '未ログイン';
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const SORT_OPTIONS = [
    { value: 'username_asc', label: 'ユーザID（A-Z）' },
    { value: 'username_desc', label: 'ユーザID（Z-A）' },
    { value: 'fullName_asc', label: '氏名（A-Z）' },
    { value: 'fullName_desc', label: '氏名（Z-A）' },
    { value: 'email_asc', label: 'メール（A-Z）' },
    { value: 'email_desc', label: 'メール（Z-A）' },
    { value: 'lastLoginAt_desc', label: '最終ログイン（新しい順）' },
    { value: 'lastLoginAt_asc', label: '最終ログイン（古い順）' }
  ];

  return (
    <div className="px-8 py-6">
      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">ユーザー管理</h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: '1rem',
          alignItems: 'end'
        }}>
          {/* キーワード検索 */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>キーワード検索</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="氏名、ID、メール、部署で検索"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* ステータス */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>ステータス</label>
            <select
              value={statusFilter === undefined ? '' : statusFilter ? 'true' : 'false'}
              onChange={(e) => setStatusFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">すべて</option>
              <option value="true">有効のみ</option>
              <option value="false">無効のみ</option>
            </select>
          </div>

          {/* ロール */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>ロール</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">すべてのロール</option>
              {availableRoles.map(role => (
                <option key={role.roleCode} value={role.roleCode}>
                  {role.roleName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ソート選択と件数表示 */}
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 7L12 3L16 7M8 17L12 21L16 17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              paddingLeft: '0.75rem',
              paddingRight: '2rem',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            fontWeight: '500'
          }}>
            {users.length}件のユーザー
          </div>
        </div>
      </div>

      {/* 作成ボタン */}
      <div className="mb-4">
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          + 新規作成
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">読み込み中...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">ユーザーがありません</div>
        ) : (
          <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ユーザーID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>氏名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>メール</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>部署</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ロール</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータス</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>最終ログイン</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {users.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {user.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {user.fullName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {user.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {formatRoles(user.roles)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      user.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.active ? '有効' : '無効'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {formatLastLogin(user.lastLoginAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => handleEdit(user.userId)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      開く
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
